import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { CardComponent, CardItem, ButtonComponent, TicketClosingField, TICKET_ACCENT_BY_STATUS, TICKET_STATUS_CONFIG, TICKET_STATES_ALLOWING_ACTION, TicketDetailStore, LoadGeneralComponent, TranslationService } from '../../../shared';
import { TicketActionService } from '../../../shared/services/data/ticket-action.service';
import { ApplicantRecordService } from '../../../shared/services/data/applicant-record.service';
import { ApplicantRecordComponent } from './applicant-record/applicant-record.component';
import { QuestionnaireComponent } from './questionnaire/questionnaire.component';
import { ScoreComponent } from './score/score.component';

@Component({
  selector: 'app-ticket-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    MatIconModule,
    MatTabsModule,
    RouterModule,
    CardComponent,
    ButtonComponent,
    ApplicantRecordComponent,
    QuestionnaireComponent,
    ScoreComponent,
    LoadGeneralComponent,
  ],
  templateUrl: './ticket-detail.component.html',
  styleUrl: './ticket-detail.component.css',
})
export class TicketDetailComponent implements OnInit {
  // Services
  private store = inject(TicketDetailStore);
  private ticketActionService = inject(TicketActionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private applicantRecordService = inject(ApplicantRecordService);

  // Data Store
  public ticket = this.store.ticket;
  public locationDetail = this.store.locationDetail;
  public closingFields = this.store.closingFields;
  public ticketInformationId = this.store.ticketInformationId;
  public ticketValidationId = this.store.ticketValidationId;
  public validationTicketState = this.store.validationTicketState;
  public isLoading = this.store.isLoading;
  public approvalField = this.store.approvalField;

  public actionSuccess = signal(false);
  public activeSection = signal(0);
  public coordinates = signal<{ user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null }>({ user: null, formulario: null, evidencias: null });
  public currentOdt = signal('');
  public isActionLoading = signal(false);

  constructor() {
    effect(() => {
      const fields = this.closingFields();
      if (fields.length > 0) {
        const coords = this.applicantRecordService.buildCoordinates(fields);
        this.coordinates.set(coords);
      }
    });
  }

  ngOnInit(): void {
    const odt = this.route.snapshot.paramMap.get('odt') || '';
    const locationKey = this.route.snapshot.queryParamMap.get('locationKey') || '';
    this.currentOdt.set(odt);
    this.store.load(odt, locationKey);
  }

  public get statusConfig() { return TICKET_STATUS_CONFIG; }
  public get accentByStatus() { return TICKET_ACCENT_BY_STATUS; }

  public cardItems = computed<CardItem[]>(() => {
    const t = this.ticket();
    const loc = this.locationDetail();
    if (!t) return [];

    const fullAddress = loc
      ? [
        loc.StreetName,
        loc.StreetNumber,
        loc.ApartmentNumber ? `, ${loc.ApartmentNumber}` : '',
        loc.Neighborhood,
        loc.Municipality,
        loc.City,
        loc.State,
        loc.PostalCode,
      ].filter(Boolean).join(', ')
      : t.direccion || 'N/A';

    return [
      { icon: 'tag', label: this.translationService.instant('tickets.card.folio'), value: t.folio || this.currentOdt() },
      { icon: 'workflow', label: this.translationService.instant('tickets.card.workflow'), value: t.workflow || 'N/A' },
      { icon: 'phone', label: this.translationService.instant('tickets.card.phone'), value: loc?.PhoneNumber || t.telefono || 'N/A' },
      { icon: 'location_on', label: this.translationService.instant('tickets.card.address'), value: fullAddress, row: 3 },
      { icon: 'badge', label: this.translationService.instant('tickets.card.curp'), value: loc?.Key || t.curp || 'N/A' },
    ];
  });

  public approvalStatus = computed(() => {
    const approval = this.approvalField();
    if (approval?.value) {
      return approval.value;
    }
    return 'En Proceso';
  });

  public canPerformAction = computed(() =>
    !this.isActionLoading() &&
    !this.isLoading() &&
    !!this.ticketValidationId() &&
    !!this.approvalField() &&
    !this.actionSuccess() &&
    TICKET_STATES_ALLOWING_ACTION.includes(this.validationTicketState())
  );

  public goBack(): void {
    this.router.navigate(['/tickets']);
  }

  public onReject(): void {
    const validationId = this.store.ticketValidationId();
    const approval = this.store.approvalField();
    if (!validationId || !approval) return;

    this.isActionLoading.set(true);
    this.ticketActionService.reject(
      validationId,
      approval.fieldId,
      approval.fieldTypeId,
      approval.rejectValue,
      () => {
        this.isActionLoading.set(false);
        this.actionSuccess.set(true);
      },
      () => {
        this.isActionLoading.set(false);
      }
    );
  }

  public onApprove(): void {
    const validationId = this.store.ticketValidationId();
    const approval = this.store.approvalField();
    if (!validationId || !approval) return;

    this.isActionLoading.set(true);
    this.ticketActionService.approve(
      validationId,
      approval.fieldId,
      approval.fieldTypeId,
      approval.approveValue,
      () => {
        this.isActionLoading.set(false);
        this.actionSuccess.set(true);
      },
      () => {
        this.isActionLoading.set(false);
      }
    );
  }
}